import React, {useEffect, useMemo, useRef, useState} from "react";
import {
  sendJournalMessage,
  listJournalDays,
  readJournalDay,
  setJournalPersona,
  clearJournalDay,
} from "../api/journal";
import {localDay, previousLocalDay} from "../features/journal/day";
import {isCrisisDismissed, setCrisisDismissed} from "../features/journal/crisisDismissal";
import {FiSend, FiTrash2, FiX, FiEye, FiTarget, FiCoffee, FiWind, FiFeather} from "react-icons/fi";
import {LuCalendarRange} from "react-icons/lu";
import {APP_LOCALE} from "../utils/appLocale";
import CrisisResources from "./Journal/CrisisResources";

export default function ChatJournalPanel({state}) {
  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  // The companion flagged a risk today: the crisis resources stay on screen.
  const [crisis, setCrisis] = useState(false);
  // The patient closed them for this day; a new flagged message reopens them.
  const [crisisClosed, setCrisisClosed] = useState(false);
  const [sidebarDates, setSidebarDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isNewThread, setIsNewThread] = useState(false);
  const [persona, setPersona] = useState(null);
  const [isDatesOpen, setIsDatesOpen] = useState(false);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const closeDates = () => setIsDatesOpen(false);

  const renderWithBold = (text) => {
    try {
      const parts = [];
      let lastIndex = 0;
      const regex = /\*\*(.+?)\*\*/g;
      let match;
      let key = 0;
      while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          parts.push(text.slice(lastIndex, match.index));
        }
        parts.push(<strong key={"b-" + key++}>{match[1]}</strong>);
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < text.length) parts.push(text.slice(lastIndex));
      return parts;
    } catch {
      return [text];
    }
  };

  const pid = state?.patient?.pid;

  const dateToString = localDay;
  const todayStr = dateToString(new Date());
  const yesterdayStr = previousLocalDay(new Date());

  const formatPrettyDate = (dateStr) => {
    // dateStr expected: YYYY-MM-DD
    const date = new Date(dateStr + "T00:00:00");
    const fmt = new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
    return fmt.format(date);
  };

  const sortedDates = useMemo(() => {
    return [...sidebarDates].sort((a, b) => (a > b ? -1 : 1));
  }, [sidebarDates]);

  // Close mobile drawer on Escape
  useEffect(() => {
    if (!isDatesOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeDates();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isDatesOpen]);

  useEffect(() => {
    if (!pid) return;
    setLoading(true);
    listJournalDays(state)
      .then(setSidebarDates)
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [pid]);

  useEffect(() => {
    if (!pid || !selectedDate) return;
    setLoadingMessages(true);
    setConfirmingClear(false);
    // The server leaves out the companion's note; `crisis` is all it gives of it.
    readJournalDay(state, dateToString(selectedDate))
      .then(({entries, persona: savedPersona, crisis: flagged}) => {
        setPersona(savedPersona);
        setCrisis(flagged);
        setCrisisClosed(isCrisisDismissed(pid, dateToString(selectedDate)));
        setIsNewThread(entries.length === 0);
        if (entries.length === 0) {
          setMessages([]);
        } else {
          setMessages(
            entries.map((e) => ({
              role: e.author === "bot" ? "assistant" : "user",
              content: e.text,
            }))
          );
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoadingMessages(false));
    // Not `state`: it changes whenever the layout refreshes, and every reload
    // replaced the conversation on screen, a pending reply included.
  }, [pid, selectedDate]);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const onSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    const dateStr = dateToString(selectedDate);
    const userMsg = {role: "user", content: input.trim()};
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSending(true);
    // The conversation shows at once, the spinner where the reply will appear.
    if (isNewThread) setIsNewThread(false);
    // Insert assistant placeholder immediately so spinner appears in place
    setMessages((prev) => [
      ...prev,
      {role: "assistant", content: "", pending: true},
    ]);
    try {
      // The server saves the message and the reply, and answers the reply only.
      const {reply: visibleText, crisis: flagged} = await sendJournalMessage(state, {date: dateStr, text: userMsg.content});
      if (flagged) {
        setCrisis(true);
        setCrisisClosed(false);
        setCrisisDismissed(pid, dateStr, false);
      }
      // Stream into the last assistant placeholder using visible text only
      const typed = new Promise((resolve) => {
        let i = 0;
        let revealed = false;
        const interval = setInterval(() => {
          i += Math.max(1, Math.floor(visibleText.length / 120));
          const next = visibleText.slice(0, i);
          setMessages((prev) => {
            const copy = [...prev];
            const lastIndex = copy.length - 1;
            if (lastIndex >= 0 && copy[lastIndex].role === "assistant") {
              copy[lastIndex] = {
                ...copy[lastIndex],
                content: next,
                pending: false,
              };
            }
            return copy;
          });
          if (!revealed && next.length > 0) revealed = true;
          if (i >= visibleText.length) {
            clearInterval(interval);
            resolve();
          }
        }, 20);
      });
      await typed;
      if (!sidebarDates.includes(dateStr))
        setSidebarDates((prev) => [dateStr, ...prev]);
    } catch (err) {
      console.error(err);
      // No reply is coming: drop the spinner instead of leaving it forever.
      setMessages((prev) => prev.filter((message) => !message.pending));
    } finally {
      setSending(false);
    }
  };

  const clearTodayConversation = async () => {
    const selectedDateStr = dateToString(selectedDate);
    if (!pid || selectedDateStr !== todayStr) return;
    setConfirmingClear(false);
    try {
      await clearJournalDay(state, selectedDateStr);
      setMessages([]);
      setIsNewThread(true);
      setPersona(null);
      // The crisis resources stay: a clear hides the conversation, not them.
      setSidebarDates((prev) => prev.filter((d) => d !== selectedDateStr));
    } catch (err) {
      console.error(err);
    }
  };

  const closeCrisis = () => {
    setCrisisClosed(true);
    setCrisisDismissed(pid, dateToString(selectedDate), true);
  };

  const savePersona = async (key) => {
    const dateStr = dateToString(selectedDate);
    try {
      await setJournalPersona(state, dateStr, key);
      setPersona(key);
    } catch (err) {
      console.error(err);
    }
  };

  const DatesList = ({onSelectDate}) => {
    return (
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="px-3 py-2 text-xs text-gray-500">Loading…</div>
        )}
        {!loading && sortedDates.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-500">
            No past chats yet
          </div>
        )}
        <ul className="px-2 py-2 space-y-1">
          {sortedDates.map((d) => (
            <li key={d}>
              <button
                type="button"
                className={[
                  "w-full text-left rounded-lg px-3 py-2.5 text-sm border transition-colors",
                  d === dateToString(selectedDate)
                    ? "bg-white text-slate-900 shadow-sm border-gray-200"
                    : "text-slate-700 hover:bg-gray-100 border-transparent",
                ].join(" ")}
                onClick={() => onSelectDate(new Date(d + "T00:00:00"))}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium leading-5 truncate">
                      {d === todayStr
                        ? "Today"
                        : d === yesterdayStr
                        ? "Yesterday"
                        : ""}
                    </div>
                    <div className="text-xs text-slate-500 leading-4 truncate">
                      {formatPrettyDate(d)}
                    </div>
                  </div>
                  <div className="text-xs text-slate-400 tabular-nums">
                    {d.slice(5)}
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="flex h-full w-full overflow-hidden bg-[#f9fafb] text-slate-800 rounded-lg">
      {/* Mobile dates drawer */}
      {isDatesOpen && (
        <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal>
          <button
            type="button"
            className="absolute inset-0 bg-black/30"
            onClick={closeDates}
            aria-label="Close dates drawer"
          />
          <aside className="absolute inset-y-0 left-0 w-72 max-w-[85vw] flex flex-col bg-gray-50 border-r border-gray-200 shadow-xl">
            <div className="h-14 flex items-center justify-between px-3 border-b border-gray-200">
              <div className="text-sm font-medium text-slate-700">Chats</div>
              <button
                type="button"
                onClick={closeDates}
                className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-gray-200 bg-white hover:bg-gray-50"
                aria-label="Close"
              >
                <FiX />
              </button>
            </div>
            <DatesList
              onSelectDate={(d) => {
                setSelectedDate(d);
                closeDates();
              }}
            />
          </aside>
        </div>
      )}

      {/* Sidebar — only show on desktop when there are dates */}
      {(loading || sortedDates.length > 0) && (
        <aside className="hidden md:flex flex-none md:w-fit flex-col bg-gray-50 border-r border-gray-200">
          <DatesList onSelectDate={(d) => setSelectedDate(d)} />
        </aside>
      )}

      {/* Chat area */}
      <section className="flex-1 flex flex-col min-w-0 bg-[#f9fafb]">
        <div className="border-b border-gray-100 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <button
                type="button"
                className="md:hidden inline-flex items-center justify-center h-8 w-8 rounded-md text-slate-400 hover:text-slate-600 hover:bg-gray-100 transition-colors"
                onClick={() => setIsDatesOpen(true)}
                aria-label="Open dates drawer"
              >
                <LuCalendarRange className="text-sm" />
              </button>
              <div className="text-xs font-medium text-slate-500 truncate">
                {new Date(selectedDate).toLocaleDateString(APP_LOCALE, {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                })}
              </div>
            </div>
            {dateToString(selectedDate) === todayStr ? (
              confirmingClear ? (
                // Clear hides the day from the patient; the care team keeps it.
                <div className="flex items-center gap-2" role="group" aria-label="Confirm clear">
                  <span className="text-xs text-slate-500">Hide today's conversation? Your care team can still see it.</span>
                  <button
                    type="button"
                    onClick={() => setConfirmingClear(false)}
                    className="rounded-md px-2.5 py-1.5 text-xs text-slate-600 hover:bg-gray-100 transition-colors duration-150"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={clearTodayConversation}
                    className="rounded-md bg-slate-900 px-2.5 py-1.5 text-xs text-white hover:bg-slate-700 transition-colors duration-150"
                  >
                    Clear
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmingClear(true)}
                  className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs text-slate-400 hover:text-slate-600 hover:bg-gray-100 transition-colors duration-150"
                  title="Clear today's conversation"
                >
                  <FiTrash2 className="text-[11px]" />
                  Clear
                </button>
              )
            ) : (
              !isNewThread && (
                <button
                  type="button"
                  onClick={() => setSelectedDate(new Date())}
                  className="inline-flex items-center gap-2 rounded-md border border-transparent bg-[#6772e5] px-3 py-1.5 text-sm text-white hover:bg-[#5b63d3] shadow-sm transition-all duration-150"
                  title="Start a new chat"
                >
                  New chat
                </button>
              )
            )}
          </div>
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto bg-[#f9fafb]">
          {isNewThread && !loadingMessages ? (
            <div className="h-full flex items-center justify-center px-4 py-8">
              <div className="w-full max-w-2xl mx-auto">
                {/* A cleared or empty day the companion flagged keeps them too. */}
                {crisis && !crisisClosed && (
                  <div className="mb-5">
                    <CrisisResources onDismiss={closeCrisis} />
                  </div>
                )}
                {!persona ? (
                  <div className="space-y-5">
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#6772e5]/10 mb-3">
                        <FiFeather className="text-lg text-[#6772e5]" />
                      </div>
                      <h2 className="text-base font-semibold text-slate-900">
                        How would you like to journal today?
                      </h2>
                      <p className="text-sm text-slate-500 mt-1">
                        Pick a style that fits your mood
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <button
                        type="button"
                        onClick={() => savePersona("insight")}
                        className="group rounded-lg border border-gray-200 bg-white p-3.5 text-left hover:border-[#6772e5] hover:bg-[#6772e5]/[0.02] focus:outline-none transition-all duration-150"
                      >
                        <div className="flex items-center gap-2.5">
                          <div className="h-8 w-8 rounded-lg bg-[#6772e5]/10 flex items-center justify-center flex-shrink-0">
                            <FiEye className="text-sm text-[#6772e5]" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-slate-900 group-hover:text-[#6772e5] transition-colors">
                              Insight Guide
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                              Name emotions and discover patterns
                            </div>
                          </div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => savePersona("practical")}
                        className="group rounded-lg border border-gray-200 bg-white p-3.5 text-left hover:border-[#6772e5] hover:bg-[#6772e5]/[0.02] focus:outline-none transition-all duration-150"
                      >
                        <div className="flex items-center gap-2.5">
                          <div className="h-8 w-8 rounded-lg bg-[#e8a23e]/10 flex items-center justify-center flex-shrink-0">
                            <FiTarget className="text-sm text-[#e8a23e]" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-slate-900 group-hover:text-[#6772e5] transition-colors">
                              Practical Coach
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                              Set tiny goals for the next 24–72 hrs
                            </div>
                          </div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => savePersona("gentle")}
                        className="group rounded-lg border border-gray-200 bg-white p-3.5 text-left hover:border-[#6772e5] hover:bg-[#6772e5]/[0.02] focus:outline-none transition-all duration-150"
                      >
                        <div className="flex items-center gap-2.5">
                          <div className="h-8 w-8 rounded-lg bg-[#34a853]/10 flex items-center justify-center flex-shrink-0">
                            <FiCoffee className="text-sm text-[#34a853]" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-slate-900 group-hover:text-[#6772e5] transition-colors">
                              Gentle Check‑In
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                              Quick mood snapshot, light and validating
                            </div>
                          </div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => savePersona("mindfulness")}
                        className="group rounded-lg border border-gray-200 bg-white p-3.5 text-left hover:border-[#6772e5] hover:bg-[#6772e5]/[0.02] focus:outline-none transition-all duration-150"
                      >
                        <div className="flex items-center gap-2.5">
                          <div className="h-8 w-8 rounded-lg bg-[#5b9bd5]/10 flex items-center justify-center flex-shrink-0">
                            <FiWind className="text-sm text-[#5b9bd5]" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-slate-900 group-hover:text-[#6772e5] transition-colors">
                              Mindfulness
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                              Grounding exercises, present‑moment focus
                            </div>
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={onSend} className="text-center">
                    <div className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#6772e5]/10 mb-3">
                      {persona === "insight" ? <FiEye className="text-lg text-[#6772e5]" /> : persona === "practical" ? <FiTarget className="text-lg text-[#e8a23e]" /> : persona === "gentle" ? <FiCoffee className="text-lg text-[#34a853]" /> : <FiWind className="text-lg text-[#5b9bd5]" />}
                    </div>
                    <h2 className="text-base font-semibold text-slate-900 mb-4">
                      {(() => {
                        const map = {
                          insight: "What feelings are most present right now?",
                          practical:
                            "What’s one thing you want to improve in the next 1–3 days?",
                          gentle:
                            "Mood and energy 0–10, and one thing that went okay?",
                          mindfulness:
                            "Want a 60-sec grounding, or jump right in?",
                        };
                        return map[persona] || "How are you feeling today?";
                      })()}
                    </h2>
                    <div className="relative max-w-lg mx-auto">
                      <label htmlFor="first-journal-input" className="sr-only">
                        Share your feelings
                      </label>
                      <input
                        id="first-journal-input"
                        type="text"
                        className="w-full rounded-full border border-gray-200 bg-white px-4 py-3 text-sm placeholder-gray-400 focus:outline-none focus:border-[#6772e5]/40 transition-colors"
                        placeholder="Share what’s on your mind…"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        disabled={sending}
                      />
                      <button
                        type="submit"
                        disabled={sending || !input.trim()}
                        className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-9 w-9 rounded-full bg-[#6772e5] text-white disabled:opacity-40 transition-opacity"
                        title="Send"
                      >
                        <FiSend className="text-sm" />
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          ) : null}
          {messages.map((m, i) => (
              <div
                key={i}
                className={m.role === "assistant" ? "bg-[#f9fafb]" : "bg-white"}
              >
                <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8 py-4">
                  <div
                    className={
                      "flex " +
                      (m.role === "assistant" && m.pending
                        ? "items-center"
                        : "items-start") +
                      " gap-3"
                    }
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {m.role === "assistant" ? (
                        <div className="h-6 w-6 rounded-full bg-white border border-gray-200 flex items-center justify-center">
                          <img
                            src="/favicon.png"
                            alt="Assistant"
                            className="h-3.5 w-3.5"
                          />
                        </div>
                      ) : (
                        <div className="h-6 w-6 rounded-full bg-[#6772e5] text-white flex items-center justify-center text-[10px] font-semibold">
                          {(state?.patient?.fName || "U")
                            .slice(0, 1)
                            .toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="whitespace-pre-wrap leading-relaxed text-sm text-slate-700 flex-1">
                      {m.role === "assistant" && m.pending ? (
                        <div className="flex items-center h-6">
                          <span
                            className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-500"
                            aria-label="Loading"
                          ></span>
                        </div>
                      ) : (
                        renderWithBold(m.content || "")
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
        {!isNewThread && (
          <div className="border-t border-gray-100 bg-white">
            <form onSubmit={onSend} className="max-w-3xl mx-auto px-3 py-3">
              <div className="relative">
                <textarea
                  rows={1}
                  placeholder="Continue journaling…"
                  className="w-full resize-none rounded-xl bg-gray-50 border border-gray-200 text-slate-800 text-sm placeholder-gray-400 px-4 py-2.5 pr-11 focus:outline-none focus:border-[#6772e5]/40 focus:bg-white transition-all"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onInput={(e) => {
                    e.currentTarget.style.height = "auto";
                    e.currentTarget.style.height =
                      Math.min(160, e.currentTarget.scrollHeight) + "px";
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      onSend(e);
                    }
                  }}
                  disabled={sending}
                />
                <button
                  type="submit"
                  disabled={sending}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-8 w-8 rounded-lg bg-[#6772e5] text-white disabled:opacity-40 transition-opacity"
                  title="Send"
                >
                  <FiSend className="text-xs" />
                </button>
              </div>
              {crisis && !crisisClosed && <CrisisResources onDismiss={closeCrisis} />}
              <p className="mt-1.5 text-center text-[10px] text-gray-400">
                For journaling support only — not medical advice
              </p>
            </form>
          </div>
        )}
      </section>
    </div>
  );
}
